import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import { Tab } from '@material-ui/core';
import DownloadIcon from '@material-ui/icons/GetApp';
import { TabContext, TabList } from '@material-ui/lab';

import { CommunicationMessageTypes } from '../../helpers/constants';
import { LogPayload } from '../../helpers/logger';
import {
  base64ToUint8Array,
  composeFunctions,
  decompressString,
  stringifyWithoutCycles,
} from '../../helpers/utils';
import useCommunicator from '../../hooks/useCommunicator';
import { ILogControlMessage } from '../../types/communicator-types';
import DebugTab from './DebugTab';

interface DebugLogger {}

const StyledH1 = styled.h1`
  // margin-left: 10px;
  background-color: #f5f5f5;
  color: #9e9e9e;
  padding: 10px;
  svg.download {
    float: right;
    margin-right: 10px;
    cursor: pointer;
  }
  .file-input {
    margin-left: 10px;
  }
`;

type ILogData = {
  [k: string]: LogPayload;
};

const DebugLogger: React.FC<DebugLogger> = () => {
  const [logData, setLogData] = useState<ILogData>({});

  const [activeTab, setActiveTab] = useState('');
  const logCommunicator = useCommunicator<ILogControlMessage>(
    CommunicationMessageTypes.LogControl
  );
  useEffect(() => {
    logCommunicator?.receiveMessage((message) => {
      if (message.type === 'LogResponse') {
        const { payload } = message;
        const data = composeFunctions<string, Uint8Array, string, LogPayload>(
          base64ToUint8Array,
          decompressString,
          JSON.parse
        )(payload);
        console.log('parsed JSON', data);
        setLogData((existing) => {
          const existingVal = existing[data.id] ?? {};

          if (existingVal.logs?.length === data.logs.length) {
            return existing;
          }
          return {
            ...existing,
            [data.id]: data,
          };
        });
        setActiveTab((current) => (current === '' ? data.id : current));
      }
    });
  }, [logCommunicator]);
  useEffect(() => {
    logCommunicator?.postMessage({ type: 'LogRequest' });
    // Pooling has issues with memory leak.
    // const interval = setInterval(() => {
    //   logCommunicator?.postMessage({ type: 'LogRequest' });
    // }, 500);
    // return () => {
    //   clearInterval(interval);
    // };
  }, [logCommunicator]);
  const handleTabChange = (event: React.ChangeEvent<any>, newValue: string) => {
    setActiveTab(newValue);
  };
  const downloadLogs = () => {
    const blob = new Blob([stringifyWithoutCycles(logData)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `logs_${Date.now()}.json`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  // Load logs from user input file
  const loadLogs = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const data = e.target?.result;
      if (typeof data === 'string') {
        const parsedData = JSON.parse(data);
        setLogData(parsedData);
      }
    };
    reader.readAsText(file);
  };

  return (
    <div>
      <main>
        <StyledH1>
          Debug Logger
          {Object.keys(logData).length > 0 && (
            <DownloadIcon onClick={downloadLogs} className="download" />
          )}
          <input className="file-input" type="file" onChange={loadLogs} />
        </StyledH1>
        {activeTab !== '' && (
          <TabContext value={activeTab}>
            <TabList onChange={handleTabChange}>
              {Object.keys(logData)
                .sort((a, b) => {
                  const val1 = logData[a].name || a;
                  const val2 = logData[b].name || b;
                  return val1 > val2 ? -1 : 1;
                })
                .map((id) => {
                  const value = logData[id];
                  return <Tab key={id} label={value.name || id} value={id} />;
                })}
            </TabList>
            {Object.values(logData).map((item) => {
              return (
                <DebugTab key={item.id} tabValue={item.id} logs={item.logs} />
              );
            })}
          </TabContext>
        )}
      </main>
    </div>
  );
};

export default DebugLogger;
